/**
 * Is this form field still carrying the label it was born with?
 *
 * `formTypes.ts` gives every component a default `label` equal to its own type name
 * — a dropped Text Field arrives labelled "Text Field". The canvas row draws the
 * label on the left and the type on the right, so an un-renamed field printed the
 * same string twice and the row carried no information at all (owner screenshot
 * 2026-08-23 19:48).
 *
 * The row keeps the TYPE, because "see the shape" is half of what the row is for.
 * It is the label that says nothing while it is still the default, so an
 * un-renamed field reads as untitled — which is also the prompt to name it, the
 * way Jotform's default label is a question rather than the control's name.
 *
 * EXACT match on the trimmed strings, not `includes`: "Text Field for the reason"
 * is a deliberate name that happens to contain the type, and treating it as unnamed
 * would erase a label the admin typed.
 *
 * ONLY FOR SYSTEM COMPONENTS (2026-08-27, owner screenshot).
 * ---------------------------------------------------------
 * The rule above is true of a seeded built-in and FALSE of one the tenant created: a
 * custom component's `name` is `deriveComponentName(label)`, which slugs the label — and
 * for a ONE-WORD label the slug is the label. Measured:
 *
 *     "test"           -> "test"            equal   -> was read as UNTITLED
 *     "Amount"         -> "amount"          equal   -> was read as UNTITLED
 *     "Notes"          -> "notes"           equal   -> was read as UNTITLED
 *     "Invoice Number" -> "invoice_number"  differs -> survived
 *
 * So every single-word custom component showed correctly in the palette and became
 * "Untitled field" the moment it landed on the canvas, while the properties panel beside
 * it displayed the label the admin had typed. The label was never lost; only this
 * function's reading of it was wrong.
 *
 * `isSystem` DEFAULTS TO FALSE — i.e. to "the admin named this" — deliberately. A caller
 * that forgets to pass it gets a label shown as typed, which is the harmless direction;
 * defaulting the other way would reproduce the defect for every legacy field restored from
 * a saved definition, where the flag is not present.
 */
/**
 * `componentName` ACCEPTS SEVERAL CANDIDATES (2026-08-28, owner screenshot 17:26).
 * ------------------------------------------------------------------------------
 * The rule above was written when the canvas fed on `formTypes.ts`, whose catalog declares
 * `name: 'Text Field'` — the label itself. The canvas now fills from the LIVE API through
 * `toLegacy`, where `name` is the backend SLUG (`text_field`, `worker_reference`,
 * `duration_field`). `'Text Field' !== 'text_field'`, so from that change onward this
 * function returned false for every seeded component and the defect it exists to prevent
 * was fully back: the owner's screenshot shows `1 Worker … Worker` and
 * `3 Length of time … Length of time`.
 *
 * It is not a near miss on a few types. `getFormComponentDisplay` resolves a system
 * component's label from `processes:liveDisplay.systemFormComponents.<key>.label`, and
 * `translateFormComponentCode` resolves the row's TYPE column from the SAME key — so the
 * two strings are identical BY CONSTRUCTION. Measured across the map: 15 of 15 seeded
 * types, in all nine locales.
 *
 * So the honest comparison is against the string the row actually PRINTS beside the label,
 * and the slug is kept as a second candidate because it is what still matches the static
 * catalog. One function, several candidates, rather than two functions that can disagree.
 */
export function isUnnamedField(
  label: string | undefined | null,
  componentName:
    | string
    | undefined
    | null
    | ReadonlyArray<string | undefined | null>,
  isSystem: boolean | undefined = false,
): boolean {
  const trimmed = (label ?? '').trim();
  if (!trimmed) return true;
  if (!isSystem) return false;
  const candidates = (Array.isArray(componentName) ? componentName : [componentName])
    .map((candidate) => (candidate ?? '').trim())
    .filter((candidate) => candidate !== '');
  return candidates.some((candidate) => trimmed.toLowerCase() === candidate.toLowerCase());
}

/** What one canvas row prints. `typeCaption: null` means "do not print a type column". */
export interface CanvasRowText {
  label: string;
  /** `label` is the not-named-yet PROMPT rather than text anyone authored. */
  placeholder: boolean;
  typeCaption: string | null;
}

/**
 * The row's presentation, as one decision instead of three conditionals in JSX.
 *
 * ONE PRINCIPLE: a row never prints the same string twice. What it prints INSTEAD depends
 * on whether the admin can act on the row — which is the owner's decision of 2026-08-28
 * ("分成两条"), and the two branches are not two conventions but one principle meeting two
 * different affordances:
 *
 *   LOOSE FIELD — selectable, renameable in the properties panel. The label slot reads the
 *   untitled PROMPT and the type column stays. That is the 2026-08-23 decision verbatim,
 *   and the prompt is legitimate because renaming is something the admin can actually do.
 *
 *   GROUP MEMBER — inside a hard container: 整组删除，不支持表单内组件单独编辑 (2026-08-27).
 *   No renaming is possible, so a prompt asks for an action the surface refuses. Worse, a
 *   group of seeded components became three identical "Untitled field" rows, which hides
 *   what the group even contains. So the identity is printed ONCE, in the label slot, and
 *   the type column — which would only repeat it — is dropped.
 *
 * Extracted rather than left inline because a decision expressed as JSX cannot be asserted:
 * `formCanvas.test.ts` could only scan the file for strings, and a scan is what let the
 * regression above sit behind an assertion that stayed green (svdy-ui §16).
 *
 * PURE: the caller passes the already-translated `typeCaption` and `untitledLabel`, so this
 * needs no i18n instance and the nine locales cannot change its behaviour.
 */
export function canvasRowText(input: {
  label: string | undefined | null;
  /** Backend slug, kept as a candidate for the static catalog's label-as-name shape. */
  componentName: string | undefined | null;
  isSystem: boolean | undefined;
  /** The row's type column text, ALREADY translated and WITHOUT any choice-count suffix. */
  typeCaption: string;
  /** ACCEPTED AND DELIBERATELY IGNORED since 2026-09-01.
   *
   * It used to decide whether a type-equals-label row got the muted "you did not choose this name"
   * style — loose rows yes, group members no. That produced two appearances for one fact on a
   * single canvas, and the muted branch covered 11 of 17 rows, so it was the page's normal look
   * rather than a signal.
   *
   * Kept in the contract rather than removed because every caller has it to hand and a future
   * reader will reach for it again; `canvasRowTextIgnoresGrouping` in the test file proves the two
   * outputs are identical, so re-reading it here means changing that assertion and saying why. */
  grouped: boolean;
  untitledLabel: string;
}): CanvasRowText {
  const { label, componentName, isSystem, typeCaption, untitledLabel } = input;
  const authored = (label ?? '').trim();
  if (!isUnnamedField(authored, [componentName, typeCaption], isSystem)) {
    return { label: authored, placeholder: false, typeCaption };
  }
  // A LABEL THAT EQUALS ITS TYPE IS NOT UNTITLED. Print the real name ONCE, drop the duplicate type
  // column, and style it like every other row.
  //
  // THREE ROUNDS ON THIS ROW, and each fix created the next complaint. Worth the length, because the
  // lesson is not about labels:
  //
  //   1. `Length of time … Length of time` — the name and the type column printed the same string
  //      (2026-08-28). Read as duplicated.
  //   2. `Untitled field … Length of time` — the duplicate was suppressed by declaring such a field
  //      NAMELESS. Read as a lie, and it was: the owner's saved draft had 12 fields and ZERO blank
  //      labels.
  //   3. the real name, in muted italic, meaning "you did not choose this name" — MY fix earlier
  //      today. The owner's next screenshot: 为啥form的添加以后，一些颜色是深色，一些是浅色？
  //
  // Round 3 failed on ARITHMETIC I did not do. 11 of the 17 rows on that canvas have a
  // type-equals-label name, because that is what dropping a component sets. So the "exception"
  // style covered the MAJORITY, and a style the majority carries is not a signal — it is the page's
  // normal appearance, and `text.disabled` + italic makes that appearance read as "these fields are
  // switched off". It also disagreed with the group rows, which were never muted, so one canvas had
  // two conventions for one fact.
  //
  // So: no style at all. The invitation to rename is the row being clickable, which it already is,
  // and the editor is where naming happens. `placeholder` survives only for a type that resolves to
  // nothing — see below — which is the one case where the words really are a prompt.
  if (typeCaption.trim() !== '') {
    return { label: typeCaption, placeholder: false, typeCaption: null };
  }
  // A type with no resolvable caption cannot be printed at all, so the prompt is the only thing
  // left — it at least says "this row is not describing itself" rather than rendering an empty
  // cell. This is now the ONLY path to the untitled WORDS and to the muted style.
  return { label: untitledLabel, placeholder: true, typeCaption };
}
